import React, { useState, useEffect, useRef, Suspense, useCallback, useMemo, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useNavigate } from '../hooks/useNavigate';
import { Alert, Button, Modal } from 'antd';
import { LogoutOutlined, MenuOutlined } from '@ant-design/icons';
import { RootState } from '../redux/store';
import { logout } from '../redux/slice/userSlice';
import { clearTeamLeadState } from '../redux/slice/teamLeadSlice';
import { clearJudgeState } from '../redux/slice/judgeSlice';
import { clearSelectedCoordinatorFest } from '../redux/slice/coordinatorSlice';
import { clearCustomRoleFest } from '../redux/slice/customRoleSlice';
import { usePermissions } from '../hooks/usePermissions';
import useIsMobile from '../hooks/useIsMobile';
import { onSubscriptionBlocked, POST } from '../utils/apiRequest';
import { setAccessToken, setRefreshToken, getRefreshToken } from '../utils/tokenStore';
import API from '../config/apis';
import SideBar from './SideBar';
import LayoutHeader from './LayoutHeader';
// BreadcrumbNav is rendered inside PageHeader on each page — not globally here

interface AppLayoutProps {
  children: React.ReactNode;
}

const ContentSkeleton = memo(() => (
  <div className="animate-pulse space-y-4">
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-2">
        <div className="h-[26px] w-44 rounded-lg bg-muted" />
        <div className="h-4 w-72 rounded-md bg-muted/60" />
      </div>
      <div className="h-9 w-28 shrink-0 rounded-lg bg-muted" />
    </div>
    <div className="h-11 rounded-xl bg-muted/50" />
    <div className="h-[58vh] rounded-xl bg-muted/40" />
  </div>
));

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  // Set centrally by apiRequest when the API refuses a request because the
  // institution's subscription has lapsed, so the notice is identical wherever
  // in the app the block happens.
  const [subscriptionBlock, setSubscriptionBlock] = useState<
    { code: string; message: string } | null
  >(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useSelector((state: RootState) => state.User);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchPermissions } = usePermissions();
  const lastFetchRef = useRef<number>(0);

  // Helper: fetch only if roleId present and cooldown elapsed
  const maybeFetch = useCallback(() => {
    if (!user?.roleId) return;
    const now = Date.now();
    if (now - lastFetchRef.current < 30_000) return; // 30-second cooldown
    lastFetchRef.current = now;
    fetchPermissions();
  }, [user?.roleId, fetchPermissions]);

  // 1. Fetch on mount / roleId change (covers login + page refresh)
  useEffect(() => {
    if (user?.roleId) {
      lastFetchRef.current = Date.now();
      fetchPermissions();
    }
  }, [user?.roleId, fetchPermissions]);

  // 2. Re-fetch on every route navigation (with 30-second cooldown to avoid spam)
  useEffect(() => {
    maybeFetch();
    setMobileOpen(false); // close mobile sidebar on navigation
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3. Re-fetch when the user returns to this tab (admin may have updated permissions)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        maybeFetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [maybeFetch]);

  const handleLogoutClick = useCallback(() => {
    setLogoutModalOpen(true);
  }, []);

  const handleLogoutConfirm = useCallback(async () => {
    setLogoutModalOpen(false);
    // Invalidate refresh token on the server before clearing local state
    try { await POST(API.LOGOUT, { refreshToken: getRefreshToken() }); } catch { /* best-effort */ }
    setAccessToken(null);
    setRefreshToken(null);
    dispatch(logout());
    dispatch(clearTeamLeadState());
    dispatch(clearJudgeState());
    dispatch(clearSelectedCoordinatorFest());
    dispatch(clearCustomRoleFest());
    navigate('/', { replace: true });
  }, [dispatch, navigate]);

  // Stable reference — prevents LayoutHeader from re-rendering on every navigation
  const headerUser = useMemo(
    () => (user ? { name: user.name, role: user.role } : null),
    [user?.name, user?.role], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    onSubscriptionBlocked((block) => setSubscriptionBlock(block));
    return () => onSubscriptionBlocked(null);
  }, []);

  return (
    <div className="flex min-h-screen bg-background">
      <SideBar
        collapsed={collapsed}
        onCollapse={setCollapsed}
        user={user}
        onLogout={handleLogoutClick}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div
        className="flex min-w-0 flex-1 flex-col transition-[margin] duration-200"
        style={{ marginLeft: isMobile ? 0 : (collapsed ? 72 : 250) }}
      >
        <LayoutHeader
          user={headerUser}
          onLogout={handleLogoutClick}
          isMobile={isMobile}
          onMenuClick={() => setMobileOpen(true)}
        />
        <main className="flex-1">
          {subscriptionBlock && (
            <div className="px-6 pt-6 max-md:px-3 max-md:pt-3">
              <Alert
                type="warning"
                showIcon
                closable
                onClose={() => setSubscriptionBlock(null)}
                message={subscriptionBlock.message}
                description="Choose a plan to restore access to your fests, events and reports."
                action={
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      setSubscriptionBlock(null);
                      navigate('/admin/subscription');
                    }}
                  >
                    Renew Plan
                  </Button>
                }
              />
            </div>
          )}
          <div className="p-6 max-md:p-3 page-enter min-h-[calc(100vh-60px)]">
            {/* BreadcrumbNav is rendered inside PageHeader */}
            <Suspense fallback={<ContentSkeleton />}>
              {children}
            </Suspense>
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal
        open={logoutModalOpen}
        onCancel={() => setLogoutModalOpen(false)}
        footer={null}
        centered
        width={400}
        closable={false}
        styles={{ mask: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
      >
        <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'color-mix(in srgb, var(--destructive) 10%, transparent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <LogoutOutlined style={{ fontSize: 28, color: 'var(--destructive)' }} />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: 'var(--foreground)' }}>
            Confirm Logout
          </h3>
          <p style={{ fontSize: 14, color: 'var(--muted-foreground)', marginBottom: 28 }}>
            Are you sure you want to logout? You will need to sign in again to access your account.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => setLogoutModalOpen(false)}
              style={{
                flex: 1, padding: '10px 24px', borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.12)', background: 'transparent',
                color: 'var(--foreground)', fontSize: 14, fontWeight: 500,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseOver={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
              onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
            >
              Cancel
            </button>
            <button
              onClick={handleLogoutConfirm}
              style={{
                flex: 1, padding: '10px 24px', borderRadius: 8,
                border: 'none', background: 'var(--destructive)',
                color: 'var(--destructive-foreground)', fontSize: 14, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)',
              }}
              onMouseOver={e => (e.currentTarget.style.background = 'var(--destructive)')}
              onMouseOut={e => (e.currentTarget.style.background = 'var(--destructive)')}
            >
              <LogoutOutlined style={{ marginRight: 6 }} />
              Logout
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AppLayout;

